/**
 * AddonCreateDialog - Dialog para cadastro manual de add-on
 * Permite criar add-ons manualmente quando integração falha
 * 
 * @version 1.0
 * @date 2025-12-19
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertTriangle } from 'lucide-react';
import { 
  useAddonManagement, 
  useTenantsList, 
  useAddonsList, 
  useProvidersList 
} from '@/hooks/useAddonManagement';

/**
 * Schema de validação do formulário
 */
const formSchema = z.object({
  tenantId: z.string().min(1, 'Selecione um tenant'),
  addonId: z.string().min(1, 'Selecione um add-on'),
  providerId: z.string().optional(),
  quantity: z.number().min(1, 'Quantidade mínima é 1').max(100, 'Quantidade máxima é 100'),
  stripeSubscriptionId: z.string().optional(),
  stripeSubscriptionItemId: z.string().optional(),
  notes: z.string().optional(),
});

type FormData = z.infer<typeof formSchema>;

interface AddonCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function AddonCreateDialog({ open, onOpenChange, onSuccess }: AddonCreateDialogProps) {
  const { t } = useTranslation('admin');
  const { createAddon, isCreating } = useAddonManagement();
  const { data: tenants, isLoading: isLoadingTenants } = useTenantsList();
  const { data: addons, isLoading: isLoadingAddons } = useAddonsList();
  const { data: providers, isLoading: isLoadingProviders } = useProvidersList();

  // Estado para controlar se provider é obrigatório
  const [requiresProvider, setRequiresProvider] = useState(false);

  // Formulário
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      tenantId: '',
      addonId: '',
      providerId: '',
      quantity: 1,
      stripeSubscriptionId: '',
      stripeSubscriptionItemId: '',
      notes: '',
    },
  });

  /**
   * Handler quando add-on muda - verifica se precisa de provider
   */
  const handleAddonChange = (addonId: string) => {
    const selectedAddon = addons?.find(a => a.id === addonId);
    setRequiresProvider(selectedAddon?.addon_type === 'inbox');
    
    if (selectedAddon?.addon_type !== 'inbox') {
      form.setValue('providerId', '');
    }
  };

  /**
   * Submit do formulário
   */
  const onSubmit = async (data: FormData) => {
    // Valida provider se necessário
    if (requiresProvider && !data.providerId) {
      form.setError('providerId', { message: 'Provider é obrigatório para add-ons do tipo inbox' });
      return;
    }

    try {
      await createAddon({
        tenantId: data.tenantId,
        addonId: data.addonId,
        providerId: data.providerId || null,
        quantity: data.quantity,
        stripeSubscriptionId: data.stripeSubscriptionId || null,
        stripeSubscriptionItemId: data.stripeSubscriptionItemId || null,
        notes: data.notes,
      });

      form.reset();
      onSuccess?.();
    } catch (error) {
      // Erro já tratado no hook
    }
  };

  /**
   * Fecha dialog e reseta form
   */
  const handleClose = (open: boolean) => {
    if (!open) {
      form.reset();
      setRequiresProvider(false);
    }
    onOpenChange(open);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>
            {t('addonManagement.create.title', 'Cadastro Manual de Add-on')}
          </DialogTitle>
          <DialogDescription>
            {t('addonManagement.create.description', 'Crie um add-on manualmente para um tenant. Use apenas quando a integração automática falhar.')}
          </DialogDescription>
        </DialogHeader>

        <Alert variant="destructive" className="bg-amber-500/10 border-amber-500/30">
          <AlertTriangle className="h-4 w-4 text-amber-500" />
          <AlertDescription className="text-amber-700 dark:text-amber-300">
            Esta ação irá criar registros no banco de dados sem validação com Stripe. Use com cautela.
          </AlertDescription>
        </Alert>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            {/* Tenant */}
            <FormField
              control={form.control}
              name="tenantId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tenant *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o tenant..." />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {isLoadingTenants ? (
                        <SelectItem value="loading" disabled>Carregando...</SelectItem>
                      ) : (
                        tenants?.map(tenant => (
                          <SelectItem key={tenant.id} value={tenant.id}>
                            {tenant.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Add-on */}
            <FormField
              control={form.control}
              name="addonId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Add-on *</FormLabel>
                  <Select 
                    onValueChange={(value) => {
                      field.onChange(value);
                      handleAddonChange(value);
                    }} 
                    value={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecione o add-on..." />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {isLoadingAddons ? (
                        <SelectItem value="loading" disabled>Carregando...</SelectItem>
                      ) : (
                        addons?.map(addon => (
                          <SelectItem key={addon.id} value={addon.id}>
                            {addon.name} ({addon.addon_type})
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Provider (condicional) */}
            {requiresProvider && (
              <FormField
                control={form.control}
                name="providerId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Provider *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o provider..." />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {isLoadingProviders ? (
                          <SelectItem value="loading" disabled>Carregando...</SelectItem>
                        ) : (
                          providers?.map(provider => (
                            <SelectItem key={provider.id} value={provider.id}>
                              {provider.name}
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    <FormDescription>
                      Obrigatório para add-ons do tipo inbox
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            )}

            {/* Quantidade */}
            <FormField
              control={form.control}
              name="quantity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Quantidade *</FormLabel>
                  <FormControl>
                    <Input 
                      type="number" 
                      min={1} 
                      max={100}
                      {...field}
                      onChange={(e) => field.onChange(parseInt(e.target.value) || 1)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Stripe Subscription ID (opcional) */}
            <FormField
              control={form.control}
              name="stripeSubscriptionId"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Stripe Subscription ID</FormLabel>
                  <FormControl>
                    <Input placeholder="sub_xxxxx" {...field} />
                  </FormControl>
                  <FormDescription>
                    Opcional. Preencha se souber o ID da subscription no Stripe.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Observações */}
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Observações</FormLabel>
                  <FormControl>
                    <Textarea 
                      placeholder="Motivo do cadastro manual, ticket de suporte, etc..."
                      rows={3}
                      {...field} 
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => handleClose(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isCreating}>
                {isCreating && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Criar Add-on
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
